import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface TBNode {
  id: string;
  code: string;
  name: string;
  type: string;
  parent_id: string | null;
  balance: string;
  children: TBNode[];
}

interface TBNodeRowProps {
  node: TBNode;
  depth: number;
}

function TBNodeRow({ node, depth }: TBNodeRowProps) {
  const [expanded, setExpanded] = useState(true);
  const hasChildren = node.children.length > 0;
  const balance = BigInt(node.balance);
  const isZero = balance === 0n;

  return (
    <>
      <tr className="border-b text-sm hover:bg-slate-50">
        <td
          className="py-1 font-mono text-xs text-slate-500"
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {hasChildren && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mr-1 text-slate-400 hover:text-slate-600"
            >
              {expanded ? '▾' : '▸'}
            </button>
          )}
          {node.code}
        </td>
        <td className="px-2 py-1" style={{ paddingLeft: `${depth * 16 + 8}px` }}>
          {node.name}
        </td>
        <td className="px-2 py-1 text-xs text-slate-400">{node.type}</td>
        <td className={`px-2 py-1 text-right font-mono text-sm ${isZero ? 'text-slate-300' : ''}`}>
          {node.balance}
        </td>
      </tr>
      {expanded &&
        hasChildren &&
        node.children.map((child) => <TBNodeRow key={child.id} node={child} depth={depth + 1} />)}
    </>
  );
}

export function TrialBalance() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));

  const { data, isLoading, error } = useQuery({
    queryKey: ['trial-balance', activeOrgId, asOf],
    queryFn: () =>
      api<{ as_of: string; tree: TBNode[] }>(`/bookkeeping/reports/trial-balance?as_of=${asOf}`),
    enabled: !!activeOrgId && !!asOf,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('trialBalance.title')}</h1>
      </div>

      <div className="flex items-center gap-3">
        <label className="text-sm text-slate-600">
          {t('trialBalance.asOf')}:
          <input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
            className="rounded border px-2 py-1 text-sm"
          />
        </label>
      </div>

      {isLoading && <p className="text-sm text-slate-500">{t('common:loading')}</p>}
      {error && (
        <p className="text-sm text-red-600">
          {(error as { message?: string }).message ?? 'Error al cargar balance'}
        </p>
      )}

      {data && (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-2 py-2">{t('trialBalance.columns.code')}</th>
                <th className="px-2 py-2">{t('trialBalance.columns.name')}</th>
                <th className="px-2 py-2">{t('trialBalance.columns.type')}</th>
                <th className="px-2 py-2 text-right">{t('trialBalance.columns.balance')}</th>
              </tr>
            </thead>
            <tbody>
              {data.tree.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-sm text-slate-400">
                    {t('trialBalance.empty')}
                  </td>
                </tr>
              ) : (
                data.tree.map((node) => <TBNodeRow key={node.id} node={node} depth={0} />)
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
